import { useMemo } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { logFieldCalendarCrash } from "./fieldCalendarDiagnostics";
import {
  coerceValidDay,
  dateKey,
  formatMinutesLabel,
  getVisitLane,
  getVisitPrimaryAssignee,
  isValidDate,
  layoutTimedVisits,
  leaveBlocksForDate,
  sanitizeVisits,
  visitPlacementDate,
  visitTimeRange,
  type TimedVisitLayout,
} from "./fieldCalendarUtils";
import { blockClassForLeave, blockClassForVisit, laneAccentClass } from "./visitBlockStyles";
import type { CalendarLeaveBlock, CalendarVisit } from "./fieldCalendarTypes";

type Props = {
  day: Date;
  visits: CalendarVisit[] | null | undefined;
  leaveBlocks: CalendarLeaveBlock[] | null | undefined;
  myScheduleUserId?: number;
  onVisitClick: (id: number) => void;
};

function laneLabel(lane: ReturnType<typeof getVisitLane>): string {
  if (lane === "urgent") return "Urgent";
  if (lane === "service") return "Service";
  if (lane === "installation") return "Installation";
  if (lane === "programming") return "Programming";
  if (lane === "completed") return "Completed";
  return "Others";
}

function timeLabelForVisit(visit: CalendarVisit): { start: string; end: string; range: ReturnType<typeof visitTimeRange> } {
  const range = visitTimeRange(visit);
  if (range) {
    return {
      start: formatMinutesLabel(range.startMins),
      end: formatMinutesLabel(range.endMins),
      range,
    };
  }
  const win = visit.arrivalWindow?.trim();
  if (win) {
    return { start: win, end: "", range: null };
  }
  return { start: "Time TBD", end: "", range: null };
}

export function FieldCalendarExecutionPanel({
  day,
  visits,
  leaveBlocks,
  myScheduleUserId,
  onVisitClick,
}: Props) {
  const safeDay = coerceValidDay(day);
  const dayKey = dateKey(safeDay);
  const dayTitle = isValidDate(safeDay)
    ? safeDay.toLocaleDateString("en-SG", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "Selected day";

  const dayVisits = useMemo(() => {
    try {
      return sanitizeVisits(visits)
        .filter((v) => dateKey(visitPlacementDate(v)) === dayKey)
        .sort(
          (a, b) => (visitTimeRange(a)?.startMins ?? 9999) - (visitTimeRange(b)?.startMins ?? 9999),
        );
    } catch (error) {
      logFieldCalendarCrash("executionPanel.dayVisits", error);
      return [];
    }
  }, [visits, dayKey]);

  const dayLeave = leaveBlocksForDate(leaveBlocks, safeDay, myScheduleUserId);

  const layoutByVisitId = useMemo(() => {
    try {
      const layouts = layoutTimedVisits(dayVisits);
      const map = new Map<number, TimedVisitLayout>();
      for (const row of layouts) {
        if (row?.visit?.id != null) map.set(row.visit.id, row);
      }
      return map;
    } catch (error) {
      logFieldCalendarCrash("executionPanel.layoutTimedVisits", error);
      return new Map<number, TimedVisitLayout>();
    }
  }, [dayVisits]);

  const dayStart = 8 * 60;
  const dayEnd = 19 * 60;

  return (
    <section className="border-t-2 border-slate-300 bg-slate-50">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-2">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-bold text-slate-900">{dayTitle}</h2>
          <p className="text-[11px] font-semibold text-slate-500">
            Tap a row to open · {dayVisits.length} visit{dayVisits.length === 1 ? "" : "s"}
            {dayLeave.length > 0 ? ` · ${dayLeave.length} leave` : ""}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold text-white">
          Run sheet
        </span>
      </div>

      <div className="min-h-[120px] space-y-2 px-3 py-3">
        {dayLeave.map((lb) => (
          <div
            key={lb.id ?? `leave-${lb.userId}`}
            className={cn(
              "rounded-lg px-3 py-2 text-sm font-semibold",
              blockClassForLeave("dispatch"),
            )}
          >
            On leave — {lb.userName ?? "Staff"}
            {lb.period !== "full_day" ? ` (${lb.period})` : ""}
          </div>
        ))}

        {dayVisits.length === 0 && dayLeave.length === 0 && (
          <p className="py-8 text-center text-sm text-slate-500">
            No appointments on this day. Select another date or turn off &quot;Mine only&quot;.
          </p>
        )}

        {dayVisits.map((visit) => {
          if (visit?.id == null) return null;
          const lane = getVisitLane(visit);
          const assignee = getVisitPrimaryAssignee(visit);
          const site = visit.customerName?.trim() || visit.customerAddress?.trim() || "—";
          const layout = layoutByVisitId.get(visit.id);
          const { start, end, range } = timeLabelForVisit(visit);
          const indent = layout ? layout.column * (layout.columnCount > 1 ? 14 : 0) : 0;

          const span = dayEnd - dayStart;
          const barLeft =
            range && span > 0 ? ((range.startMins - dayStart) / span) * 100 : 0;
          const barWidth =
            range && span > 0
              ? Math.max(((range.endMins - range.startMins) / span) * 100, 4)
              : 0;

          return (
            <button
              key={visit.id}
              type="button"
              onClick={() => onVisitClick(visit.id)}
              style={{ marginLeft: indent }}
              className={cn(
                "relative w-full rounded-lg text-left shadow-sm transition-opacity active:opacity-90",
                blockClassForVisit(lane, undefined, "dispatch"),
                layout && layout.columnCount > 1 && layout.column > 0 && "ring-1 ring-slate-200/80",
              )}
            >
              <div className="flex gap-2 p-3">
                <div className="w-[72px] shrink-0">
                  <p className="text-xs font-bold leading-tight">{start}</p>
                  {end ? (
                    <p className="text-[10px] font-semibold opacity-80">{end}</p>
                  ) : null}
                </div>
                <div className={cn("w-1 shrink-0 rounded-full", laneAccentClass(lane))} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold leading-tight">{site}</p>
                  <p className="mt-0.5 truncate text-[11px] font-medium opacity-90">
                    {assignee.userName} · {visit.visitNumber ?? "SVR"} · {laneLabel(lane)}
                  </p>
                  {lane === "urgent" && (
                    <span className="mt-1 inline-block rounded bg-red-700 px-1.5 py-px text-[9px] font-bold uppercase text-white">
                      Urgent
                    </span>
                  )}
                  {range ? (
                    <div className="relative mt-2 h-1.5 overflow-hidden rounded-full bg-black/10">
                      <div
                        className={cn("absolute inset-y-0 rounded-full", laneAccentClass(lane))}
                        style={{ left: `${barLeft}%`, width: `${barWidth}%` }}
                      />
                    </div>
                  ) : null}
                </div>
                <ChevronRight className="h-5 w-5 shrink-0 self-center opacity-50" />
              </div>
              {layout && layout.columnCount > 1 && (
                <span className="absolute right-2 top-2 rounded bg-black/10 px-1 text-[9px] font-bold">
                  overlap
                </span>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}
